import { Buffer } from 'buffer'
import { IhmLoginTokenProvider } from '@api/utils/IhmLoginTokenProvider'
import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { IIBTokenProvider } from '@api/utils/IIBTokenProvider'
import { IIBServiceClient } from '@api/IIBServiceClient'

export class TokenUtils {
    /**
     * Extracts the expiration time from the IhmLogin accessToken and verifies if it's expired. Note that it returns false if it
     * can't determine the expiration time.
     */
    public static isTokenExpired (accessToken: string): boolean {
        const payload = TokenUtils.extractPayload(accessToken)
        if (!payload) {
            console.error('ATBase: No payload in the token')
            return false
        }

        const tokenExpirationTimeSeconds = payload.exp
        if (tokenExpirationTimeSeconds === undefined || tokenExpirationTimeSeconds === null) {
            console.error('ATBase: Got token without expiration')
            return false
        }

        const tokenExpirationTimeMillis = tokenExpirationTimeSeconds * 1000
        const currentTime = new Date().getTime()

        // this should never be false because when we get here, a service call already rejected us with one of
        // the LOGOUT_EXCEPTIONS. We're doing this as a fail-safe in case a service returns one of those
        // exceptions for something else.
        const isExpired = currentTime > tokenExpirationTimeMillis
        console.info('ATBase: isTokenExpired: currentTime =', currentTime,
            'tokenExpirationTimeMillis =', tokenExpirationTimeMillis, 'isExpired =', isExpired)
        return isExpired
    }

    /**
     * Extracts the expiration time from the IIB accessToken and verifies if it's expired. Note that it returns true if it
     * can't determine the expiration time.
     */
    public static async isTokenExpiredIIB () {
        const expiry = await IIBTokenProvider.getTokenExpiry()
        console.log('ATBase: IIB Token logoutOnTokenExpiry expiry: ',expiry)
        return (!expiry || expiry < Date.now())
    }

    /**
     * This function checks if ihmLogin or IIB token has expired.
     * If expired then it redirects to logout.
     */
    public static async logoutOnTokenExpiry () {
        console.info('ATBase: logoutOnTokenExpiry called.')
        if (EnvironmentDetail.isIIBAuthN()) {
            const currentTime = Date.now()
            const expiry = await IIBTokenProvider.getTokenExpiry()
            const refreshTokenCallbackTime = await IIBTokenProvider.getRefreshTokenCallbackTime()
            console.log('ATBase: IIB Token logoutOnTokenExpiry expiry: ',expiry)
            if (refreshTokenCallbackTime <= currentTime && currentTime < expiry) {
                console.log('ATBase: IIB Token logoutOnTokenExpiry time to refresh the tokens currentTime: ',currentTime)
                IIBServiceClient.refreshTokens()
                return
            }
            if (!expiry || expiry < Date.now()) {
                console.log('ATBase: IIB Token has expired. Hence logging out')
                IIBServiceClient.logout()
                return
            }
        }
    }

    /**
     * It extracts the user alias from the email present in the IIB token
     */
    public static async getUserAliasFromIIBToken () {
        const idToken = await IIBTokenProvider.getIdToken()
        const decryptedIdToken = TokenUtils.extractPayload(idToken)
        const identitiesField = 'identities'
        const emailField = 'email'
        const providerNameField = 'providerName'
        const preferredUsernameField = 'preferred_username'
        const userIdField = 'userId'
        const idp = decryptedIdToken[identitiesField][0][providerNameField]

        // In 1P and 3P vendor, the email field is alias@company.com, so extract the alias
        // In 2P, the email sometimes exists, so use the email field if it exists, otherwise,
        // use the userId in identities field
        // In 3P, the email field does not exist, so use the username from preferred_username
        let alias
        const email = decryptedIdToken[emailField]
        if (idp === 'Ihm1pIdentityProvider' || idp === 'externalVendor') {
            alias = email ? email.split('@')[0] : null
        } else if (idp === 'wfm') {
            const userId = decryptedIdToken[identitiesField][0][userIdField]
            alias = userId ? userId.split('@')[0] : null
        } else {
            alias = decryptedIdToken[preferredUsernameField] ?? null
        }

        console.log('ATBase userAlias extracted from IIB token: ', alias)
        return alias
    }

    /**
     * Parses the token's payload and extracts the payload. Returns null if it can't parse the token.
     */
    private static extractPayload (accessToken: string) {
        const parts = accessToken.split('.')
        if (parts.length !== 3) {
            console.error('ATBase: Got invalid token')
            return null
        }

        try {
            const payload = Buffer.from(parts[1], 'base64').toString('utf8') // base64 decode
            return JSON.parse(payload)
        } catch (error) {
            console.error('ATBase: Could not parse the payload of the token')
            return null
        }
    }
}
